import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {select, setProps} from '@ngneat/elf';
import {toSignal} from '@angular/core/rxjs-interop';
import {tap} from 'rxjs';
import {AuthService, isTokenExpired} from './auth-service';
import {authStore, User} from './auth.store';
import {LoginRequestDto, PermissionFlags, PmccWebAccessDeniedError} from './auth.types';
import {PERMISSION_LANDING_ROUTES} from './landing-routes';
import {AppConfigService} from '@/core/config/app-config.service';
import {buildPermissions, isSuperAdminUser, moduleKey, parseRoles} from './login-response.mapper';

export type PermissionAction = 'view' | 'create' | 'edit' | 'delete';

const ACTION_FLAG: Record<PermissionAction, keyof PermissionFlags> = {
    view: 'canView',
    create: 'canCreate',
    edit: 'canUpdate',
    delete: 'canDelete',
};

/** Auth state + orchestration. Components use this; AuthService is HTTP-only. */
@Injectable({providedIn: 'root'})
export class AuthRepository {
    private readonly authService = inject(AuthService);
    private readonly router = inject(Router);
    private readonly appConfig = inject(AppConfigService);

    // Store selectors
    readonly user$ = authStore.pipe(select((state) => state.user));
    readonly token$ = authStore.pipe(select((state) => state.token));
    readonly role$ = authStore.pipe(select((state) => state.role));
    readonly roles$ = authStore.pipe(select((state) => state.roles ?? []));
    readonly isLoggedIn$ = authStore.pipe(select((state) => !!state.token));
    readonly permissions$ = authStore.pipe(select((state) => state.permissions));

    // Signals
    readonly user = toSignal(this.user$);
    readonly role = toSignal(this.role$);
    readonly roles = toSignal(this.roles$, {initialValue: [] as string[]});
    readonly isLoggedIn = toSignal(this.isLoggedIn$, {initialValue: false});
    readonly permissions = toSignal(this.permissions$, {initialValue: {} as Record<string, PermissionFlags>});

    /** A stored token that has expired counts as logged out (the guard sends the user to login). */
    isAuthenticated(): boolean {
        const token = authStore.query((state) => state.token);
        if (token && isTokenExpired(token)) {
            authStore.reset();
            return false;
        }
        return !!token;
    }

    hasRole(requiredRoles: string[]): boolean {
        const roles = authStore.query((state) => state.roles ?? []);
        return requiredRoles.some((role) => roles.includes(role));
    }

    /**
     * Checks a module permission against the given map (pass `permissions()` to stay reactive).
     * The superadmin login is allowed everything, and is the only one allowed to delete (whatever the `Permission[]`
     * or `enforcePermissions` say).
     */
    can(moduleName: string, action: PermissionAction, permissions = authStore.query((s) => s.permissions)): boolean {
        const isSuperAdmin = authStore.query((s) => s.isSuperAdmin);
        if (action === 'delete') return isSuperAdmin;
        if (this.appConfig.config?.enforcePermissions === false || isSuperAdmin) {
            return true;
        }
        return permissions[moduleKey(moduleName)]?.[ACTION_FLAG[action]] ?? false;
    }

    canView(moduleName: string): boolean {
        return this.can(moduleName, 'view');
    }

    canCreate(moduleName: string): boolean {
        return this.can(moduleName, 'create');
    }

    canUpdate(moduleName: string): boolean {
        return this.can(moduleName, 'edit');
    }

    canDelete(moduleName: string): boolean {
        return this.can(moduleName, 'delete');
    }

    getLandingRouteByPermission(): string {
        const landing = PERMISSION_LANDING_ROUTES.find(({module}) => !module || this.canView(module));
        return landing?.route ?? '/no-access';
    }

    // Orchestration
    /** Calls LoginWeb, stores token, profile, roles and permissions, then opens the landing page. */
    login(credentials: LoginRequestDto) {
        return this.authService.login(credentials).pipe(
            tap((response) => {
                if (!response.AccessToken) {
                    throw new Error('Login response did not include an access token');
                }
                if (!response.IsPMCCWebApplicable) {
                    throw new PmccWebAccessDeniedError();
                }
                const roles = parseRoles(response.Role);
                const user: User = {
                    id: response.UserID,
                    userName: response.UserName,
                    name: response.Name || response.UserName,
                    designation: response.Designation,
                    employeeId: response.EmployeeID,
                    supervisor: response.Supervisor,
                    supervisorId: response.SupervisorID,
                    companyId: response.CompanyID,
                    folderPath: response.FolderPath,
                };
                authStore.update(
                    setProps({
                        token: response.AccessToken,
                        refreshToken: null,
                        user,
                        role: roles[0] ?? null,
                        roles,
                        permissions: buildPermissions(response.Permission),
                        isSuperAdmin: isSuperAdminUser(response.UserName),
                    })
                );
                this.router.navigateByUrl(this.getLandingRouteByPermission());
            })
        );
    }

    logout(): void {
        this.authService.logout();
    }

    markSessionExpired(): void {
        this.authService.markSessionExpired();
    }
}
